import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { colors, fonts, glyphs } from "../../../Styles";
import { Button, NewModal, MainContentPaddingTypes, NewSelect } from "../..";
import { useAppSelector } from "../../../Store";
import { Graph_QL } from "../../../Services";
import { useAnimal } from "../../../Services/Agrisys_GqlDataCaller/AnimalDataCalls/useAnimal";
import { useParams } from "react-router";
import { useSection } from "../../../Services/Agrisys_GqlDataCaller/LocationFeedingCalls/useSections";
import { useSystem } from "../../../Services/Agrisys_GqlDataCaller/SystemCalls/useSystem";
import { LfLocation } from "../../../Services/Agrisys_GqlDataCaller/LocationFeedingCalls/GQLCalls/getLfStructureCall";

const SYSTEM_ID = 1;

interface AddAnimalToPen_Props{
  activePen?: LfLocation;
  isModalOpen: boolean
  goBack: () => void,
  onChange: (e: number) => void,
}
/**
 * MoveAnimalToPen
 * @param activePen The pen where an animal is going to be assigned to.
 * @param goBack Function to go back.
 * @returns a modal with a pen where the animal, based on selected animal No, is going to be assigned to.
 */
export default function AddAnimalToPen({activePen, isModalOpen, goBack, onChange}:AddAnimalToPen_Props) {
  const {t} = useTranslation()
  const [selectedAnimal, setAnimal] = useState<number>();
  const { systemId: urlSystemId } = useParams(); // TODO routing changes in order to get a correct system info
  const systemId = urlSystemId ? +urlSystemId : SYSTEM_ID;

  console.log("animal from select", selectedAnimal)

  const farm = useAppSelector(({activeData}) => activeData.farm) ?? "";
  const domain = useMemo(() => (Graph_QL.ObjToString({domain: farm})),[farm]);
  const connection = useMemo(() => (Graph_QL.create({domain: farm})),[farm]);
  
  const { call: getAnimalsInfo, data: animalsState, maintainEntries: updateAnimalInfo } = useAnimal(connection);
  const { call: getLfSectionsInfo, data: lfSectionsState } = useSection(connection);
  const { data: systemsState } = useSystem(connection);

  const systemName = useMemo(() => 
    systemsState[domain]?.data.find((system) => system.systemId === systemId)?.name, 
    [systemsState, domain, systemId]
  );
  
  const sections = useMemo(() => lfSectionsState[domain]?.data, [domain, lfSectionsState]);
  
  useEffect(() => {
    getLfSectionsInfo({ ids: [systemId] }, connection);
    // eslint-disable-next-line
  }, []);
  
  const findSectionByPenId = useCallback((penId: number) => {
    if (!sections) return null;
    const section = sections.find((section) =>
      section.section_locations.some((location) => location.location_id === penId)
    );
    return section?.name ?? null;
  }, [sections]);
  
  const sectionName = activePen ? findSectionByPenId(activePen.location_id) : undefined;
  
  const animals = useMemo(() => {
    return animalsState && animalsState[domain] ? animalsState[domain].data : []
  },[animalsState, domain])

  useEffect(() => {
    if (!(animalsState[domain])) {
      getAnimalsInfo({fetchAmount: 50}, connection);
    }
  },[animalsState, connection, domain, getAnimalsInfo])

  const animalOptions = useMemo(() => {
  const options = animals.map(animal => ({
    label: String(animal.animal_number),
    id: animal.animal_id
  }));
  return options
  }, [animals] )

  return (
    <NewModal 
      modalState={isModalOpen}
      header={t("animalPages.animal.addAnimal", {defaultValue: "Add Animal"})}
      subHeader={`${systemName} ${sectionName}`}
      overrides={{ 
        mainContentPadding: MainContentPaddingTypes.secondary
      }}
      predefinedButtons={{
        onGoBack: goBack
      }}
      mainContent={
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "60px", marginTop: "25px"}}>
          <div style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "20px"}}>
          <p style={{...fonts.paragraph, ...fonts.fontSize3, color: colors.greyLightTertiary}}>{t("animalPages.animal.animalId", {defaultValue: "Animal No."})}</p>
            <NewSelect
              options={animalOptions}
              fieldOverrides={{
                fieldWidth: "137.5px"
              }}
              isSearchable
              onChange={(e) => setAnimal(Number(e?.id))}
            />
            <glyphs.Arrow/>
            <p style={{...fonts.paragraph, ...fonts.fontSize3, color: colors.greyLightTertiary}}>{t("animalPages.animal.pen", {defaultValue: "Pen"})} {activePen?.name}</p>
          </div>
          <Button 
            content={`${t("animalPages.animal.addAnimalToPen", {defaultValue: "Add Animal to Pen"})}`}
            onClick={() => {
              goBack();
              // if (updateAnimalInfo) {
              //   updateAnimalInfo({data: [{animalId: selectedAnimal, location: activePen?.location_id}]})
              // }
              onChange(selectedAnimal ?? 0);
            }}
          />
        </div>
      }
    />
  )
}